import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, signal, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { forkJoin } from 'rxjs';
import { ProjectsService } from 'src/app/akita/project/state/projects.service';
import { SharedProjectService } from 'src/app/core/service/api/shared-project.service';
import { CachedService } from 'src/app/core/service/common/cached.service';
import { modalConfig, ModalEnum } from 'src/app/core/util/modalConfig';
import { ChatChannelSidebarComponent } from '../chat-channel-sidebar/chat-channel-sidebar.component';
import { Globals } from 'src/app/globals';
import { htmlRemoval, itemsAlgo, removeAndpaces } from 'src/app/core/util/commentBox';
import collect from 'collect.js';
import { showFeedbackSaved } from 'src/app/core/util/notification';
import posthog from 'posthog-js';

interface comment {
  message : string;
  collaborator: any[];
  items: any [];
}

@Component({
  selector: 'app-shared-project-chat-panel',
  templateUrl: './shared-project-chat-panel.component.html',
  styleUrls: ['./shared-project-chat-panel.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SharedProjectChatPanelComponent {
  selectedTab: 'chatten' | 'volgers' = 'chatten';
  isDropdownOpen: boolean = false;
  commentEntity: comment = { message: '', collaborator: [], items: [] };
  isLoading = signal(true);
  public collaboratorList:any[]=[];
  public collaboratorUsers:any[]=[];
  public users:any[]=[];
  messages = signal([]);
  isSending = signal(false);
  mentionConfig: any;
  @ViewChild('scrollContainer') private scrollContainer!: ElementRef;
  @ViewChild('textareaRef') private textareaRef!: ElementRef;

  constructor(public globals: Globals, @Inject(MAT_DIALOG_DATA) public data: any, private cd: ChangeDetectorRef, private _cachedService: CachedService, private sharedProjectsService: SharedProjectService, private _sharedService: ProjectsService, private dialog: MatDialog, private dialogRef: MatDialogRef<SharedProjectChatPanelComponent>){ }

  get internalType(): string {
    return this.data?.is_main_project ? 'projects' : 'subprojects';
  }

  ngOnInit(): void {
    this.loadMessages();
  }

  isMyMessage(msg: any): boolean {
    if (this.data?.is_shared_project) {
      return this.globals.user.global_id === msg.users.id;
    } else {
      return this.globals.user.id === msg.users.id;
    }
  }

  getMessageBackgroundColor(msg: any): string {
    if (this.isMyMessage(msg)) {
      return '#2800d2';
    }
    if (this.data?.is_shared_project) {
      return 'eff0f6';
    } else {
      return '#f6f6fa';
    }
  }

  getMessageTextColor(msg: any): string {
    if (this.isMyMessage(msg)) {
      return '#ffffff';
    }
    if (this.data?.is_shared_project && !this.isMyMessage(msg)) {
      return '#000000';
    }
    return '#000000';
  }

  loadMessages(){
    if (this.data?.users && this.data?.users.length) {
      this.collaboratorList = [...this.data.users];
    }
    this.isLoading.set(true);
    forkJoin({
      users: this.data?.is_shared_project ? this._cachedService.getGlobalUsers() : this._cachedService.getUsers(),
      messages: this.data?.is_shared_project ? this.sharedProjectsService.getAllMessages('sharedProjects', this.data.id, 1) : this.sharedProjectsService.getAllInternalSubprojectMessages(this.internalType, this.data.id, 1)
    }).subscribe(({ users, messages } : any) => {
      this.collaboratorUsers = users;
      this.loadCollaborator();
      this.users = users;
      this.messages.set(messages.data);
      this.loadMentions();
      this.isLoading.set(false);
      setTimeout(() => this.scrollToBottom(), 0);
    });
  }

  ngAfterViewInit() {
    if (this.textareaRef?.nativeElement) {
      this.textareaRef.nativeElement.addEventListener('paste', (event: ClipboardEvent) => {
        event.preventDefault();
        const text = event.clipboardData?.getData('text/plain');
        if (text) {
          this.insertTextAtCursor(text);
        }
      });
    }
  }

  private insertTextAtCursor(text: string) {
    const selection = window.getSelection();
    if (!selection || !selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    range.deleteContents();
    range.insertNode(document.createTextNode(text));

    // move cursor to end
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
  }

  private loadMentions(firstLoad=[]): void {
    this.mentionConfig = {
      mentions: [
        {
          items: this.users,
          triggerChar: '@',
          labelKey: 'name',
          mentionSelect: this.onMentionSelectUser,
          allowSpace: true
        },
        {
          items: firstLoad,
          triggerChar: '#',
          labelKey: 'title_name',
          mentionSelect: this.onMentionSelectItems,
          allowSpace: true
        }
      ]
    };
  }

  public loadItems(searchTerm: string) {
    let sortingAlgo=[];
    const types=[];
    if (searchTerm.length > 0) {
      this._sharedService.itemsSearch(searchTerm).subscribe((res: any) => {
        sortingAlgo = itemsAlgo(res);
        sortingAlgo.forEach(element => {
          if (element.title_name) {
            const match = element.title_name.match(/^(.*?)(\s*\(.*\))$/); 
            if (match) {
              const mainText = match[1];  // text before brackets
              const typeText = match[2];  // text with brackets

              const truncatedMain = mainText.length > 30 ? mainText.substring(0, 30) + '...' : mainText;
              element.title_name = `${truncatedMain} ${typeText}`;
            } else {
              element.title_name = element.title_name.length > 30 ? element.title_name.substring(0, 30) + '...' : element.title_name;
            }
          }
        });
        this.mentionConfig.mentions[1].items.forEach(el=>{
          types.push(`${el.type}-${el.id}`);
        });
        sortingAlgo.forEach(element => {
          if(this.mentionConfig.mentions[1].items.length > 0){
              // eslint-disable-next-line no-empty
              if(types.includes(`${element.type}-${element.id}`)){
              }else{
                this.mentionConfig.mentions[1].items.push(element);
              }
          }else{
            this.mentionConfig.mentions[1].items.push(element);
          }
        });
        if(false){
          this.loadMentions(this.mentionConfig.mentions[1].items);
        }
        this.cd.detectChanges();
      });
    }
  }

  public onMentionSelectUser(input: any) {
    return `##${input.name}##`;
  }

  public onMentionSelectItems(input: any) {
    return `##${input.name}##`;
  }

  public itemSelected(event: any) {
    event.name = htmlRemoval(event.name);
    if (event.item) {
      this.commentEntity.items.push(event);
    } else {
      this.commentEntity.collaborator.push(event);
    }

    const code = event.code ? event.code : '';
    setTimeout(() => {
      let uniqueID = `${event.id}_${event.type}_${code}`;
      this.textareaRef.nativeElement.innerHTML = removeAndpaces(this.textareaRef.nativeElement.innerHTML).replace(
        `##${event.name}##`,
        `&nbsp;<a class="blue-text d-cursor-hand items-mentions tagged-user" target="${event.id}" type="${event.type}" title="${code}">${event.name}</a><span id="${uniqueID}">&nbsp;</span>`
      );

      this.commentEntity.message = this.textareaRef.nativeElement.innerHTML;
      this.selectEnd(uniqueID);
    }, 10);
  }

  public selectEnd(id: string) {
    let range, selection;
    const getElement = document.getElementById(id);
    if (!getElement) return;

    range = document.createRange();
    range.selectNodeContents(getElement);
    range.collapse(false);

    selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
  }

  onChangeComment(innerHtml: string) {
    if(innerHtml=='<br>'){
      this.commentEntity.message='';
      return;
    }
    this.commentEntity.message = innerHtml;
  }

  selectTab(tab: 'chatten' | 'volgers') {
    this.selectedTab = tab;
  }

  toggleChannelDropdown(){
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  sendMessage(){
    if (this.commentEntity.message.trim().length) {
        const msg = this.commentEntity.message.replace(/<br\s*\/?>/gi, '\n').replace(/&nbsp;/gi, ' ').replace(/\s+/g, ' ').trim();
        if (this.commentEntity.collaborator?.length) {
          let added = false;
          this.commentEntity.collaborator.forEach(user => {
            const exists = this.collaboratorList.some(c => c.email === user.email);
            if (!exists) {
              this.collaboratorList.push(user);
              added = true;
            }
          });
          this.addCollaborators();
        }
        this.commentEntity.message = "";
        this.textareaRef.nativeElement.innerHTML = '';
        this.isSending.set(true);
        setTimeout(() => this.scrollToBottom(), 0);
        if (this.data?.is_shared_project) { 
          this.sharedProjectsService.sendMessages('sharedProjects', this.data.id, 1, {message:{...this.commentEntity, message: msg,project:this.globals.project.name},comment_type:'comment'}).subscribe(()=>{
          this.getAllMessages();
        })
      } else {
        this.sharedProjectsService.sendInternalSubprojectMessages(this.internalType, this.data.id, 1, {message:{...this.commentEntity, message: msg,project:this.globals.project.name},comment_type:'comment'}).subscribe(()=>{
          this.getAllMessages();
        })
      }
    }
  }

  getAllMessages(){
    if (this.data?.is_shared_project) { 
      this.sharedProjectsService.getAllMessages('sharedProjects', this.data.id, 1).subscribe((resp: any)=>{
        this.isSending.set(false);
        this.messages.set(resp.data);
        setTimeout(() => this.scrollToBottom(), 0);
      })
    } else {
      this.sharedProjectsService.getAllInternalSubprojectMessages(this.internalType, this.data.id, 1).subscribe((resp: any)=>{
        this.isSending.set(false);
        this.messages.set(resp.data);
        setTimeout(() => this.scrollToBottom(), 0);
      })
    }
  }

  goBack(){
    this.dialogRef.close();
    this.dialog.open(ChatChannelSidebarComponent, modalConfig({
      data: {},
      panelClass: ['animate__animated', 'animate__slideInRight', 'chat-sidebar'],
      disableClose: false,
    }, ModalEnum.SidebarChatMediumResponsive))
  }

  private scrollToBottom(): void {
    try {
      this.scrollContainer.nativeElement.scrollTop = this.scrollContainer.nativeElement.scrollHeight;
    } catch (err) {}
  }

  public collaboratorDropdown(collaborator){
    let contains;
    if(this.collaboratorList.length>0){
      contains=collect(this.collaboratorList).contains((value, key) => value.id == collaborator.id);
      if(!contains){
        this.collaboratorList.push({id: collaborator.id, email: collaborator.email,name:collaborator.name});
      }
    }else{
      this.collaboratorList.push({id: collaborator.id, email: collaborator.email,name:collaborator.name});
    }
    this.addCollaborators();
  }

  public addCollaborators(){
    if (this.data?.is_shared_project) { 
      this.sharedProjectsService.saveCollaborators('sharedProjects',this.data.id,{collaborators:this.collaboratorList}).subscribe((res=>{
        this.loadCollaborator();
        this.loadAllMessages();
      }))
    } else {
      this._sharedService.saveCollaborators(this.internalType,this.data.id,{collaborators:this.collaboratorList}).subscribe((res=>{
        this.loadCollaborator();
        this.loadAllMessages();
      }))
    }
    posthog.capture('comment-add_collaborator', {
      eventType: this.data?.is_shared_project?'sharedProjects':this.internalType,
      eventID: this.data.id
    });
  }

  public loadCollaborator(): void{
    if (this.data?.is_shared_project) {
      this.sharedProjectsService.getAllCollaborators('sharedProjects',this.data.id).subscribe(((res:any)=>{
        this.collaboratorList=[...res.collaborators, ...this.collaboratorList];
        this.collaboratorList=[...new Map(this.collaboratorList.map(item => [item.id, item])).values()];
        this.loadCollaboratorUsers();
        this.cd.detectChanges();
      }))
    } else {
      this._sharedService.getAllCollaborators(this.internalType,this.data.id).subscribe(((res:any)=>{
        this.collaboratorList = [...res.collaborators, ...this.collaboratorList];
        const currentUser = this.users.find(u => u.email === this.globals.user.email);
        if (currentUser) {
          const exists = this.collaboratorList.some(c => c.id === currentUser.id);
          if (!exists) {
            this.collaboratorList.push(currentUser);
          }
        }
        this.collaboratorList=[...new Map(this.collaboratorList.map(item => [item.id, item])).values()];
        this.loadCollaboratorUsers();
        this.cd.detectChanges();
      }))
    }
  }

  public loadCollaboratorUsers(){
    let contains;
    const users=[];
    if(this.collaboratorList.length){
      if(this.collaboratorUsers && this.collaboratorUsers.length){
        this.collaboratorUsers.forEach(element => {
          contains=collect(this.collaboratorList).contains((value, key) => value.id == element.id);
          if(!contains){
            users.push(element);
          }
        });
        this.collaboratorUsers=users;
      }
    }
  }

  loadAllMessages(){
    if (this.data?.is_shared_project) {
      this.sharedProjectsService.getAllMessages('sharedProjects', this.data.id, 1).subscribe((res: any)=>{
        this.messages.set(res.data);
      })
    } else {
      this._sharedService.getAllMessages(this.internalType, this.data.id, 1).subscribe((res: any)=>{
        this.messages.set(res.data);
      })
    }
  }

  onEnterPress(event: KeyboardEvent) {
    if (event.shiftKey) return; // allow new line
    event.preventDefault();
    this.sendMessage();
  }

  cleanMessage(msg: string): string {
    return msg
      ?.replace(/<br\s*\/?>/gi, '\n')
      .replace(/&nbsp;/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim() || '';
  }
}
